import { IMG } from "./images";

// All Colosseum ticket templates, keyed for /tickets/:slug
export const TICKETS = [
  {
    slug: "entry",
    name: "Colosseum Entry Tickets",
    shortName: "Entry Tickets",
    tagline: "The essential ticket — self-guided access to the Colosseum, Roman Forum & Palatine Hill.",
    price: 18,
    duration: "Flexible, self-guided",
    badge: "Most affordable",
    rating: 4.7,
    reviews: 41280,
    heroImage: IMG.exteriorArena,
    cardImage: IMG.exteriorTrees,
    accent: "The straightforward way in",
    highlights: [
      "Timed entry to the Colosseum",
      "Roman Forum & Palatine Hill included",
      "Explore at your own pace",
      "Instant mobile ticket",
    ],
    overview: [
      "The Colosseum Entry Ticket is the simplest, most economical way to step inside the greatest amphitheatre ever built. It grants timed access to the Colosseum's first and second levels, where you can wander the stands the way 50,000 ancient Romans once did.",
      "Crucially, this ticket also includes a combined entry to the Roman Forum and Palatine Hill — the political and imperial heart of ancient Rome — making it remarkable value for independent travellers who like to explore at their own rhythm.",
    ],
    whyChoose: [
      { icon: "Wallet", title: "Best value entry", text: "The lowest-priced way to access all three sites: Colosseum, Forum and Palatine Hill." },
      { icon: "Compass", title: "Total freedom", text: "No fixed group, no schedule beyond your entry slot. Linger where you like and move on when you wish." },
      { icon: "Clock", title: "Quick to book", text: "Reserve a slot in minutes and receive your QR code instantly — perfect for spontaneous plans." },
    ],
    included: [
      "Timed-entry access to the Colosseum (tiers 1 & 2)",
      "Combined entry to the Roman Forum",
      "Combined entry to Palatine Hill",
      "Mobile-friendly e-ticket",
    ],
    notIncluded: [
      "Live guide or guided commentary",
      "Access to the arena floor or underground",
      "Skip-the-line security fast track",
      "Hotel pickup or transport",
    ],
    bestFor: [
      "Budget-conscious and independent travellers",
      "Visitors who prefer to explore without a group",
      "Repeat visitors who already know the history",
      "Anyone wanting maximum value across three sites",
    ],
    tips: [
      "Choose the earliest morning slot to enjoy cooler temperatures and thinner crowds.",
      "Download a free audio guide app before you arrive to add context as you explore.",
      "Visit the Forum and Palatine Hill first if your Colosseum slot is later in the day.",
    ],
    importantInfo: [
      "Entry is timed — arrive 15–20 minutes before your slot for security screening.",
      "The combined Forum/Palatine ticket is valid for one entry, same day or within 24 hours.",
      "Large bags, tripods and glass bottles are not permitted inside.",
    ],
    faqs: [
      { q: "Does the entry ticket let me skip the line?", a: "It lets you skip the ticket-purchase queue, but you still pass through the standard security line. For the fastest entry, consider a dedicated skip-the-line ticket." },
      { q: "Can I upgrade to see the arena floor later?", a: "No. Arena floor and underground access must be purchased in advance as a separate ticket or guided tour — they cannot be added on the day." },
      { q: "How long is the ticket valid?", a: "Your Colosseum entry is for a specific time slot. The Forum and Palatine portion is valid the same day or within 24 hours, depending on the partner." },
    ],
    related: ["skip-the-line", "guided-tours", "combo"],
    relatedPlanning: [
      { label: "Opening Hours", to: "/plan/opening-hours" },
      { label: "Best Time to Visit", to: "/plan/best-time-to-visit" },
      { label: "Entrances", to: "/plan/entrances" },
    ],
  },
  {
    slug: "skip-the-line",
    name: "Skip-the-Line Tickets",
    shortName: "Skip-the-Line",
    tagline: "Bypass the long queues with priority security access and a guaranteed entry time.",
    price: 27,
    duration: "Flexible entry, self-guided",
    badge: "Best seller",
    rating: 4.8,
    reviews: 63540,
    heroImage: IMG.exteriorArches,
    cardImage: IMG.womanColosseum,
    accent: "Time is precious in Rome",
    highlights: [
      "Priority, fast-track entry",
      "Guaranteed time slot",
      "Forum & Palatine Hill included",
      "Save up to 90 minutes",
    ],
    overview: [
      "In peak season, the standby queue at the Colosseum can stretch beyond ninety minutes under the Roman sun. The Skip-the-Line Ticket is designed to give you those minutes back, with priority access through a dedicated entrance and a guaranteed timed slot.",
      "It includes the same combined access to the Roman Forum and Palatine Hill as a standard ticket, so you lose none of the experience — you simply spend more of your day inside the monuments and less of it waiting in line.",
    ],
    whyChoose: [
      { icon: "Zap", title: "Skip the queues", text: "Use a priority lane and walk straight to security instead of joining the long general line." },
      { icon: "Clock", title: "Reclaim your day", text: "Save up to 90 minutes in high season — time better spent exploring or enjoying a Roman lunch." },
      { icon: "Sun", title: "Beat the heat", text: "No standing in exposed queues during the hottest hours; ideal for summer and family trips." },
    ],
    included: [
      "Priority fast-track entrance to the Colosseum",
      "Guaranteed timed-entry slot",
      "Combined entry to the Roman Forum & Palatine Hill",
      "Instant mobile e-ticket",
    ],
    notIncluded: [
      "Live guided commentary",
      "Arena floor or underground access",
      "Food and drink",
      "Transport to the site",
    ],
    bestFor: [
      "Visitors in peak season (April–October)",
      "Travellers on a tight schedule",
      "Families with children who can't queue long",
      "Anyone visiting on a hot summer day",
    ],
    tips: [
      "Even with fast-track, arrive 15 minutes early — security screening still applies to everyone.",
      "Pair this with a downloadable audio guide for context without the cost of a live tour.",
      "Mid-morning slots tend to clear security fastest once the first wave has entered.",
    ],
    importantInfo: [
      "Fast-track covers entry, not security — bag checks apply to all visitors.",
      "Your time slot has a short grace window; latecomers may be asked to wait.",
      "Re-entry is not permitted once you leave the Colosseum.",
    ],
    faqs: [
      { q: "How much time does skip-the-line actually save?", a: "It varies by season. In quiet winter months the saving may be modest, but from spring to autumn it commonly saves 45–90 minutes versus the standby line." },
      { q: "Is a guide included?", a: "No. This is a self-guided fast-track ticket. If you'd like expert commentary, choose a guided tour instead." },
      { q: "Will I skip security too?", a: "No queue-skip ticket bypasses security screening — it's mandatory for everyone. You skip the ticket and general entry lines only." },
    ],
    related: ["entry", "guided-tours", "last-minute"],
    relatedPlanning: [
      { label: "Best Time to Visit", to: "/plan/best-time-to-visit" },
      { label: "Entrances", to: "/plan/entrances" },
      { label: "Tips for Visiting", to: "/plan/tips" },
    ],
  },
  {
    slug: "guided-tours",
    name: "Guided Tours",
    shortName: "Guided Tours",
    tagline: "Bring the arena to life with an expert guide and skip-the-line access included.",
    price: 49,
    duration: "Approx. 3 hours",
    badge: "Highly rated",
    rating: 4.9,
    reviews: 38910,
    heroImage: IMG.interiorCrowd,
    cardImage: IMG.interiorGolden,
    accent: "Stories you can't read on a plaque",
    highlights: [
      "Licensed expert guide",
      "Skip-the-line entry included",
      "Forum & Palatine Hill tour",
      "Small group sizes",
    ],
    overview: [
      "A guided tour transforms a walk through old stone into a vivid journey through imperial Rome. A licensed, English-speaking guide reveals the engineering genius, the gladiatorial spectacles, and the political theatre that played out within these walls.",
      "Skip-the-line entry is built in, and most tours continue across the Roman Forum and Palatine Hill, weaving the three sites into a single, coherent story. With small group sizes and radio headsets, you'll hear every word.",
    ],
    whyChoose: [
      { icon: "GraduationCap", title: "Expert storytelling", text: "Licensed guides turn ruins into living history with context, anecdotes and answers to your questions." },
      { icon: "Users", title: "Small groups", text: "Intimate group sizes and headsets mean you stay close to your guide and never miss a detail." },
      { icon: "Zap", title: "Skip-the-line", text: "Priority access is included, so the tour begins inside the monument — not in a queue." },
    ],
    included: [
      "Licensed multilingual guide",
      "Skip-the-line Colosseum entry",
      "Guided visit of the Roman Forum & Palatine Hill",
      "Radio headset for clear audio",
    ],
    notIncluded: [
      "Arena floor or underground access (unless specified)",
      "Hotel pickup and drop-off",
      "Gratuities for your guide",
      "Food and beverages",
    ],
    bestFor: [
      "First-time visitors who want the full story",
      "History and architecture enthusiasts",
      "Travellers who prefer structure and guidance",
      "Anyone short on time who wants the highlights curated",
    ],
    tips: [
      "Wear comfortable shoes — guided routes cover uneven ancient terrain across all three sites.",
      "Bring earphones compatible with the headset for the most comfortable fit.",
      "Ask your guide for restaurant recommendations near the Forum exit.",
    ],
    importantInfo: [
      "Tours run rain or shine; bring sun protection or a light rain jacket as needed.",
      "Arrive at the meeting point 15 minutes early — tours depart promptly.",
      "Group tours typically range from 10 to 25 guests depending on the operator.",
    ],
    faqs: [
      { q: "What languages are tours offered in?", a: "English is most common, with French, Spanish, German, Italian and other languages available depending on the operator and time of day." },
      { q: "Does the guided tour include the underground?", a: "Standard guided tours cover the stands, Forum and Palatine Hill. For the underground or arena floor, choose a dedicated tour that lists those areas in its inclusions." },
      { q: "How long is the tour?", a: "Most guided tours last around three hours, covering the Colosseum, Roman Forum and Palatine Hill at a comfortable pace." },
    ],
    related: ["underground", "arena-floor", "skip-the-line"],
    relatedPlanning: [
      { label: "Tips for Visiting", to: "/plan/tips" },
      { label: "How to Reach", to: "/plan/how-to-reach" },
      { label: "Best Time to Visit", to: "/plan/best-time-to-visit" },
    ],
  },
  {
    slug: "underground",
    name: "Underground Tours",
    shortName: "Underground Tours",
    tagline: "Descend into the Hypogeum — the hidden tunnels where gladiators and beasts once waited.",
    price: 79,
    duration: "Approx. 3–3.5 hours",
    badge: "Sells out fast",
    rating: 4.9,
    reviews: 21470,
    heroImage: IMG.underground,
    cardImage: IMG.interiorArch,
    accent: "Beneath the arena",
    highlights: [
      "Access the Hypogeum tunnels",
      "Includes the arena floor",
      "Expert guide & skip-the-line",
      "Strictly limited daily places",
    ],
    overview: [
      "Below the arena floor lies the Hypogeum — a labyrinth of tunnels, cells and lift shafts where gladiators steadied their nerves and wild animals were hoisted into the spotlight. Closed to general ticket holders, it is the Colosseum's most atmospheric and sought-after experience.",
      "This guided tour takes you down into those passages and back up onto the reconstructed arena floor, where you stand at the centre of the amphitheatre. Numbers are strictly capped each day, so these tickets routinely sell out weeks ahead.",
    ],
    whyChoose: [
      { icon: "DoorOpen", title: "Restricted access", text: "Enter areas closed to standard tickets — the underground tunnels and the arena floor itself." },
      { icon: "GraduationCap", title: "Specialist guide", text: "A dedicated guide explains the ingenious machinery that staged Rome's deadliest spectacles." },
      { icon: "Sparkles", title: "Bucket-list moment", text: "Stand where gladiators stood, looking up at the tiers from the centre of the arena." },
    ],
    included: [
      "Guided access to the underground (Hypogeum)",
      "Access to the reconstructed arena floor",
      "Skip-the-line Colosseum entry",
      "Roman Forum & Palatine Hill entry",
    ],
    notIncluded: [
      "Transport and hotel pickup",
      "Food and drinks",
      "Gratuities",
      "Re-entry after the tour",
    ],
    bestFor: [
      "History lovers seeking the deepest experience",
      "Returning visitors who've seen the stands before",
      "Photographers wanting unique vantage points",
      "Anyone chasing a true bucket-list moment",
    ],
    tips: [
      "Book three to six weeks ahead — underground places are the first to disappear.",
      "The tunnels can be cool and dim; bring a light layer and watch your footing.",
      "Have your passport or ID ready; some operators verify names for restricted-area access.",
    ],
    importantInfo: [
      "Underground areas involve steps and narrow passages and may not suit limited mobility.",
      "Names on the booking may be checked against ID for security in restricted zones.",
      "Tours are non-refundable closer to the date — review cancellation terms carefully.",
    ],
    faqs: [
      { q: "Why is the underground so hard to book?", a: "The Hypogeum has a very small daily capacity for preservation and safety reasons. Demand far outstrips supply, so slots open on a rolling basis and sell out quickly." },
      { q: "Is the arena floor included with the underground tour?", a: "Yes. Underground tours almost always include the arena floor, letting you experience the amphitheatre from both below and at stage level." },
      { q: "Is it suitable for children?", a: "Older children usually enjoy it, but the tunnels involve steps and tight spaces. Check the operator's minimum age before booking." },
    ],
    related: ["arena-floor", "guided-tours", "night-tours"],
    relatedPlanning: [
      { label: "Best Time to Visit", to: "/plan/best-time-to-visit" },
      { label: "Rules & Regulations", to: "/plan/rules" },
      { label: "Tips for Visiting", to: "/plan/tips" },
    ],
  },
  {
    slug: "arena-floor",
    name: "Arena Floor Tickets",
    shortName: "Arena Floor",
    tagline: "Step onto the stage itself, through the same Gate of Death the gladiators used.",
    price: 59,
    duration: "Approx. 1.5–3 hours",
    badge: "Unforgettable",
    rating: 4.9,
    reviews: 26310,
    heroImage: IMG.interiorGolden,
    cardImage: IMG.interiorGroup,
    accent: "Centre stage",
    highlights: [
      "Walk onto the arena floor",
      "Enter via the gladiators' gate",
      "Skip-the-line included",
      "Forum & Palatine Hill access",
    ],
    overview: [
      "The arena floor is the reconstructed platform at the very heart of the Colosseum, accessed through the Porta Libitinaria — the gate through which fallen gladiators were once carried out. Standing here, surrounded by rising tiers of stone, is the closest you can come to the perspective of those who fought below the roar of the crowd.",
      "This ticket places you at the centre of the amphitheatre with skip-the-line entry and the usual combined access to the Roman Forum and Palatine Hill. It's a powerful upgrade for anyone who wants more than a view from the stands.",
    ],
    whyChoose: [
      { icon: "Footprints", title: "On the stage", text: "Stand at the centre of the arena where the spectacles unfolded — not just looking down from the seats." },
      { icon: "DoorOpen", title: "Gladiators' gate", text: "Enter through the historic Gate of Death, used to carry the fallen from the sands." },
      { icon: "Camera", title: "Iconic photos", text: "Capture the tiers sweeping up around you from a viewpoint most visitors never reach." },
    ],
    included: [
      "Access to the reconstructed arena floor",
      "Skip-the-line Colosseum entry",
      "Roman Forum & Palatine Hill entry",
      "Self-guided or guided options available",
    ],
    notIncluded: [
      "Underground (Hypogeum) access unless specified",
      "Transport and pickup",
      "Refreshments",
      "Gratuities",
    ],
    bestFor: [
      "Visitors wanting a standout experience without the full underground tour",
      "Photographers and content creators",
      "Couples and travellers marking a special trip",
      "Anyone who wants to feel the scale from the centre",
    ],
    tips: [
      "Combine with the underground if you want both perspectives in one visit.",
      "Golden hour light on the upper tiers makes for spectacular photographs.",
      "Self-guided arena access is cheaper; add a guide for the historical context.",
    ],
    importantInfo: [
      "Access to the arena floor is via steps from the lower tier.",
      "Floor capacity is limited and timed; stick to your assigned slot.",
      "Drones and tripods are not permitted on the floor.",
    ],
    faqs: [
      { q: "What's the difference between arena floor and underground tickets?", a: "The arena floor lets you stand on the central stage. The underground takes you beneath it into the tunnels. Underground tours usually include the floor, but floor-only tickets do not include the tunnels." },
      { q: "Can I get arena floor access without a guide?", a: "Yes, self-guided arena floor tickets exist. A guided version adds expert commentary and is popular with first-time visitors." },
      { q: "Is the arena floor the original ancient surface?", a: "No. The original wooden floor is long gone; what you walk on is a modern reconstructed section that recreates the ancient stage level." },
    ],
    related: ["underground", "guided-tours", "night-tours"],
    relatedPlanning: [
      { label: "Entrances", to: "/plan/entrances" },
      { label: "Tips for Visiting", to: "/plan/tips" },
      { label: "Best Time to Visit", to: "/plan/best-time-to-visit" },
    ],
  },
  {
    slug: "night-tours",
    name: "Night Tours",
    tagline: "Experience the Colosseum after dark — softly lit, hushed, and almost crowd-free.",
    shortName: "Night Tours",
    price: 69,
    duration: "Approx. 2–2.5 hours",
    badge: "Limited dates",
    rating: 4.9,
    reviews: 14920,
    heroImage: IMG.nightWarm,
    cardImage: IMG.nightDome,
    accent: "After the gates close",
    highlights: [
      "Visit after public closing",
      "Atmospheric evening lighting",
      "Minimal crowds",
      "Often includes underground",
    ],
    overview: [
      "When the daytime crowds disperse and the monument is bathed in warm light, the Colosseum reveals a different character — quieter, more intimate, and undeniably cinematic. Night tours run on select evenings after standard closing, with only a small number of guests inside.",
      "Led by an expert guide, many evening tours descend into the underground and step onto the arena floor, all under atmospheric lighting. It is widely considered the most memorable way to experience the amphitheatre.",
    ],
    whyChoose: [
      { icon: "Moon", title: "Fewer crowds", text: "Far smaller groups after hours mean space to absorb the atmosphere and take unhurried photos." },
      { icon: "Sparkles", title: "Dramatic lighting", text: "The arches glow gold against the night sky — a sight day visitors simply never see." },
      { icon: "Thermometer", title: "Cooler evenings", text: "A welcome relief in summer; explore comfortably without the midday heat." },
    ],
    included: [
      "After-hours guided Colosseum access",
      "Atmospheric evening lighting experience",
      "Underground and/or arena floor (varies by tour)",
      "Skip-the-line entry",
    ],
    notIncluded: [
      "Daytime Forum & Palatine Hill (varies by operator)",
      "Transport and pickup",
      "Food and drinks",
      "Gratuities",
    ],
    bestFor: [
      "Couples and special occasions",
      "Photographers chasing dramatic light",
      "Summer visitors avoiding the heat",
      "Anyone wanting a quieter, premium experience",
    ],
    tips: [
      "Night dates are limited and seasonal — book as far ahead as possible.",
      "Bring a light jacket; evenings can cool down even after warm days.",
      "A camera that handles low light will reward you; tripods, however, are restricted.",
    ],
    importantInfo: [
      "Night tours run on select dates, typically spring through autumn.",
      "Meeting times shift with sunset; check your confirmation carefully.",
      "Inclusions differ widely between operators — confirm whether underground is part of your tour.",
    ],
    faqs: [
      { q: "Is the underground included on night tours?", a: "Often, but not always. Some evening tours focus on the stands and arena floor, others descend into the Hypogeum. Always read the specific inclusions before booking." },
      { q: "When do night tours operate?", a: "Generally on select evenings from spring to autumn, with start times tied to sunset. Winter availability is rare." },
      { q: "Are night tours worth the extra cost?", a: "For atmosphere, photography and small-group intimacy, most travellers find the premium well worth it — it's a markedly different experience from a daytime visit." },
    ],
    related: ["underground", "arena-floor", "guided-tours"],
    relatedPlanning: [
      { label: "Opening Hours", to: "/plan/opening-hours" },
      { label: "Best Time to Visit", to: "/plan/best-time-to-visit" },
      { label: "How to Reach", to: "/plan/how-to-reach" },
    ],
  },
  {
    slug: "combo",
    name: "Combo Tickets",
    shortName: "Combo Tickets",
    tagline: "Pair the Colosseum with the Vatican, Pantheon or a hop-on bus and save.",
    price: 89,
    duration: "Half to full day",
    badge: "Great value",
    rating: 4.7,
    reviews: 18650,
    heroImage: IMG.forumWide,
    cardImage: IMG.forumColumns,
    accent: "See more, pay less",
    highlights: [
      "Colosseum + a second top sight",
      "Bundled savings vs. separate tickets",
      "Skip-the-line at both sites",
      "Efficient one-day itineraries",
    ],
    overview: [
      "Rome rewards the ambitious. Combo tickets bundle the Colosseum with another headline attraction — most often the Vatican Museums and Sistine Chapel, but also the Pantheon, Borghese Gallery or a hop-on-hop-off bus — at a price lower than booking each separately.",
      "Beyond the savings, the real benefit is logistics: pre-arranged timed entries and skip-the-line access at both sites mean you can comfortably cover two icons in a single, well-paced day.",
    ],
    whyChoose: [
      { icon: "Layers", title: "Two icons, one booking", text: "Combine the Colosseum with a second world-class sight and manage it all from a single ticket." },
      { icon: "PiggyBank", title: "Bundle savings", text: "Combos typically cost less than buying each attraction's ticket on its own." },
      { icon: "Route", title: "Smart sequencing", text: "Pre-set entry times help you build an efficient itinerary without backtracking across the city." },
    ],
    included: [
      "Colosseum, Forum & Palatine Hill entry",
      "Entry to a second attraction (e.g. Vatican Museums)",
      "Skip-the-line access where available",
      "Combined mobile tickets",
    ],
    notIncluded: [
      "Transport between the two sites",
      "Lunch and refreshments",
      "Guided commentary unless specified",
      "Gratuities",
    ],
    bestFor: [
      "Travellers with limited days in Rome",
      "First-timers wanting the big-ticket highlights",
      "Value seekers comparing total trip cost",
      "Anyone who likes a ready-made itinerary",
    ],
    tips: [
      "Schedule the Colosseum for the morning and the second site for the afternoon to manage crowds.",
      "Allow travel time between sites — the Vatican is across the city from the Colosseum.",
      "Read our 'Colosseum & Vatican in One Day' guide before you book this combo.",
    ],
    importantInfo: [
      "Each attraction has its own timed entry — note both times carefully.",
      "The Vatican enforces a strict dress code (shoulders and knees covered).",
      "Some combos are valid across two days; check validity before booking.",
    ],
    faqs: [
      { q: "Which attractions can I combine with the Colosseum?", a: "The most popular pairing is the Vatican Museums and Sistine Chapel. Others include the Pantheon, Borghese Gallery, Castel Sant'Angelo and hop-on-hop-off bus tours." },
      { q: "Is a combo ticket really cheaper?", a: "Generally yes — bundling reduces the combined price versus separate tickets, and it saves you booking time and coordination." },
      { q: "Can I do the Colosseum and Vatican in one day?", a: "Yes, with careful planning. See our dedicated one-day guide for a realistic, low-stress itinerary." },
    ],
    related: ["guided-tours", "entry", "skip-the-line"],
    relatedPlanning: [
      { label: "Nearby Attractions", to: "/plan/nearby-attractions" },
      { label: "How to Reach", to: "/plan/how-to-reach" },
      { label: "Best Time to Visit", to: "/plan/best-time-to-visit" },
    ],
  },
  {
    slug: "family",
    name: "Family Tickets",
    shortName: "Family Tickets",
    tagline: "Kid-friendly tours and easy entry designed to keep the whole family engaged.",
    price: 45,
    duration: "Approx. 2–2.5 hours",
    badge: "Family favourite",
    rating: 4.8,
    reviews: 12740,
    heroImage: IMG.touristsSmiling,
    cardImage: IMG.fountainPeople,
    accent: "Built for younger explorers",
    highlights: [
      "Engaging, kid-friendly storytelling",
      "Skip-the-line to avoid meltdowns",
      "Shorter, well-paced routes",
      "Reduced rates for children",
    ],
    overview: [
      "Visiting the Colosseum with children should be a highlight, not a test of patience. Family tickets and family-focused tours are built around shorter routes, gladiator stories that spark young imaginations, and skip-the-line access that spares everyone the queue.",
      "Children under 18 from the EU often qualify for reduced or free entry, and guides on family tours know how to pitch the history at the right level — turning ancient stone into an adventure the whole group remembers.",
    ],
    whyChoose: [
      { icon: "Baby", title: "Made for kids", text: "Storytelling and pacing designed to hold children's attention without overwhelming them." },
      { icon: "Zap", title: "No long queues", text: "Skip-the-line access keeps little ones happy and the day on schedule." },
      { icon: "Ticket", title: "Child pricing", text: "Reduced or free entry for eligible children means better value for the whole family." },
    ],
    included: [
      "Family-friendly Colosseum entry",
      "Skip-the-line access",
      "Forum & Palatine Hill entry",
      "Reduced child rates where applicable",
    ],
    notIncluded: [
      "Strollers inside restricted areas",
      "Snacks and meals",
      "Transport and pickup",
      "Gratuities",
    ],
    bestFor: [
      "Families with school-age children",
      "Multi-generational groups",
      "First-time visitors travelling with kids",
      "Parents who want a smooth, low-stress visit",
    ],
    tips: [
      "Book the first morning slot — kids are freshest and the site is coolest.",
      "Pack water and snacks for before and after; food isn't sold inside.",
      "Bring a small notebook so children can sketch the arches and gladiator gates.",
    ],
    importantInfo: [
      "Proof of age (passport/ID) is required to claim reduced child rates.",
      "Strollers may be restricted on the arena floor and in the underground.",
      "There are limited shaded areas — sun protection is essential in summer.",
    ],
    faqs: [
      { q: "Is the Colosseum free for children?", a: "EU citizens and residents under 18 typically enter free, but still need a (free or reduced) ticket reserved in advance. Non-EU children usually receive a reduced rate. Carry ID to prove age." },
      { q: "Is the underground suitable for kids?", a: "It depends on age. The tunnels involve steps and tight spaces; some operators set a minimum age. A family or standard guided tour is often a better fit for younger children." },
      { q: "Are strollers allowed?", a: "Strollers are generally fine in the main areas but may not be permitted on the arena floor or in the underground. Baby carriers are a flexible alternative." },
    ],
    related: ["skip-the-line", "guided-tours", "entry"],
    relatedPlanning: [
      { label: "Tips for Visiting", to: "/plan/tips" },
      { label: "Rules & Regulations", to: "/plan/rules" },
      { label: "Restaurants Nearby", to: "/plan/restaurants" },
    ],
  },
  {
    slug: "last-minute",
    name: "Last Minute Tickets",
    shortName: "Last Minute",
    tagline: "Sold out elsewhere? Find same-day and next-day availability from trusted partners.",
    price: 35,
    duration: "Same or next day",
    badge: "Available now",
    rating: 4.6,
    reviews: 9810,
    heroImage: IMG.exteriorClose,
    cardImage: IMG.exteriorDay,
    accent: "When plans change fast",
    highlights: [
      "Same-day & next-day slots",
      "Skip-the-line where available",
      "Instant mobile delivery",
      "No advance planning needed",
    ],
    overview: [
      "The official site has sold out, but your trip is tomorrow — this is exactly the situation last-minute tickets are built for. Our partners release short-notice availability, including guided options that hold capacity specifically for travellers booking close to their visit.",
      "Prices can be a little higher than booking weeks ahead, but you gain certainty: a confirmed slot, skip-the-line access where available, and a QR code on your phone within minutes.",
    ],
    whyChoose: [
      { icon: "Clock", title: "Book today, visit today", text: "Same-day and next-day slots when the official channels show sold out." },
      { icon: "Smartphone", title: "Instant delivery", text: "Tickets arrive by email immediately — no waiting, no print queue." },
      { icon: "Zap", title: "Still skip the line", text: "Many last-minute options retain priority entry, so you don't trade speed for spontaneity." },
    ],
    included: [
      "Short-notice Colosseum entry",
      "Skip-the-line access (where available)",
      "Forum & Palatine Hill entry on most options",
      "Instant e-ticket",
    ],
    notIncluded: [
      "Guaranteed underground or arena floor (limited)",
      "Lowest advance-purchase pricing",
      "Transport and pickup",
      "Gratuities",
    ],
    bestFor: [
      "Spontaneous travellers and weekend trips",
      "Anyone caught out by sold-out official tickets",
      "Cruise passengers and short stopovers",
      "Visitors with flexible timing",
    ],
    tips: [
      "Check availability early in the morning — fresh slots are often released overnight.",
      "Be flexible on time slot and ticket type to maximise your chances.",
      "Guided last-minute tours sometimes have space when self-guided entry is gone.",
    ],
    importantInfo: [
      "Last-minute pricing may exceed advance rates due to demand.",
      "Underground and arena floor rarely have last-minute availability.",
      "Confirm the time slot immediately — short-notice slots can vanish quickly.",
    ],
    faqs: [
      { q: "Can I really get tickets for today?", a: "Often, yes. Partners hold back inventory and release cancellations, so same-day slots regularly appear even when the official site is sold out." },
      { q: "Why are last-minute tickets more expensive?", a: "Short-notice availability and bundled guided access carry a premium. If you can plan ahead, advance tickets are usually cheaper." },
      { q: "Will I still skip the line?", a: "Many last-minute options include skip-the-line entry, but not all. Check the specific listing before you book." },
    ],
    related: ["skip-the-line", "guided-tours", "entry"],
    relatedPlanning: [
      { label: "Opening Hours", to: "/plan/opening-hours" },
      { label: "Best Time to Visit", to: "/plan/best-time-to-visit" },
      { label: "Entrances", to: "/plan/entrances" },
    ],
  },
];

export const getTicket = (slug) => TICKETS.find((t) => t.slug === slug);
export const ticketLink = (slug) => {
  const t = getTicket(slug);
  return t ? { label: t.shortName || t.name, to: `/tickets/${t.slug}` } : null;
};
